import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { createApp, type App as VueApp } from "vue";
import { createPinia } from "pinia";
import { type Router } from "vue-router";
import { createVueRouter } from "../../router/index";
import VueAppComponent from "../../components/App.vue";
import type { NavView } from "../../router/navigationTypes";
import { reportVueComponentError } from "../../observability/sentry";
import { useStore } from "../../store";
import { createAfterEachGuard } from "../router/afterEachGuard";
import { vueBridgeContainerStyle } from "./vueBridgeContainerStyle";
import { resolveVueRouterPath } from "./vueRouterPath";
import "tippy.js/dist/tippy.css";

interface VueAppBridgeProps {
  navView: NavView;
  /** React Router가 소유한 현재 pathname. */
  routePath: string;
  /**
   * React-native 경로에서 Vue 시각 트리를 숨길지 여부.
   * `true` 여도 Vue 앱은 unmount 되지 않고 `display: none` 으로만 숨겨져 Pinia 가 보존된다.
   */
  hidden?: boolean;
}

/**
 * 단일 영속 Vue 앱을 마운트하는 React 컴포넌트.
 *
 * Vue→React 마이그레이션 중, 아직 React로 재작성되지 않은 페이지를 이 브리지로 제공한다.
 * Vue 앱 인스턴스를 하나만 생성하여 라우트 변경 시에도 유지하며,
 * Pinia 스토어 상태(solver 결과, 실행 상태 등)를 보존한다.
 * navView prop으로 활성 Vue 탭을 제어한다.
 */
const AI_SETUP_NAV_VIEWS = new Set<NavView>(["ai-setup"]);

const AI_SETUP_SIDE_VIEWS = new Set([
  "ai-setup",
  "preflop-action-tree",
  "board",
  "tree-config",
  "run-solver",
  "result",
]);

const SOLVER_SIDE_VIEWS = new Set([
  "oop-range",
  "ip-range",
  "board",
  "tree-config",
  "run-solver",
  "result",
]);

export function VueAppBridge({
  navView,
  routePath,
  hidden = false,
}: VueAppBridgeProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const vueAppRef = useRef<VueApp | null>(null);
  const bridgeRouterRef = useRef<Router | null>(null);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const routePathRef = useRef(routePath);
  routePathRef.current = routePath;

  // 첫 렌더 시 Vue 앱 한 번만 마운트
  useEffect(() => {
    if (!containerRef.current) return;
    let unmounted = false;

    const initialPath = resolveVueRouterPath(navView, routePathRef.current);

    const pinia = createPinia();
    // NOTE: beforeEach 가드 미사용. navView는 VueAppBridge의 useEffect를 통해
    // React Router에서 동기화된다. beforeEach는 React StrictMode 이중 마운트 시
    // 전역 활성 Pinia를 통해 잘못된 Pinia 인스턴스에 navView를 설정하는 문제가 있었다.
    const bridgeRouter = createVueRouter();
    bridgeRouterRef.current = bridgeRouter;

    const app = createApp(VueAppComponent);
    // Vue 기본 errorHandler 는 콘솔에만 남기고 전역 error 이벤트로 올리지 않아
    // Sentry 의 전역 핸들러가 볼 수 없다. 명시적으로 전달한다.
    app.config.errorHandler = (error, _instance, vueErrorInfo) => {
      console.error("Vue bridge caught an error", error, vueErrorInfo);
      reportVueComponentError(error, vueErrorInfo);
    };
    app.use(pinia);
    app.use(bridgeRouter);
    app.mount(containerRef.current);
    vueAppRef.current = app;

    // eslint-disable-next-line react-hooks/rules-of-hooks -- Pinia store accessor (defineStore), not a React hook
    const store = useStore();
    store.navView = navView;

    // 목표 초기 경로로 이동 후 afterEach 를 등록하여 초기 "/" → "/solver"
    // 리다이렉트가 React Router 로 누출되지 않도록 한다. 가드 본체는
    // `createAfterEachGuard` 팩토리에서 관리한다 (단위 테스트 + React 네이티브 경로 가드).
    //
    // React StrictMode 이중 마운트 시 첫 라이프사이클의 가드가 정리 후에도 메모리 라우터에
    // 남아 두 번째 라이프사이클의 가드와 함께 발화하면 `navigate` 가 두 번 호출될 수 있다.
    // `bridgeRouter.afterEach` 의 반환값(등록 해제 함수) 을 정리 시 호출해 차단한다.
    let removeGuard: (() => void) | null = null;
    bridgeRouter.replace(initialPath).then(() => {
      if (unmounted) return;
      const guard = createAfterEachGuard({
        navigate: (path) => navigateRef.current(path),
        currentReactPath: () => routePathRef.current,
      });
      removeGuard = bridgeRouter.afterEach(guard);
    });

    return () => {
      unmounted = true;
      removeGuard?.();
      app.unmount();
      vueAppRef.current = null;
      bridgeRouterRef.current = null;
    };
  }, []);

  // React Router → Vue 스토어 + Vue Router로 navView 변경 사항 동기화
  useEffect(() => {
    if (!vueAppRef.current) return;
    // eslint-disable-next-line react-hooks/rules-of-hooks -- Pinia store accessor (defineStore), not a React hook
    const store = useStore();
    store.navView = navView;

    // 활성 페이지에 따라 configSource 설정 및 sideView 검증
    if (AI_SETUP_NAV_VIEWS.has(navView)) {
      store.configSource = "ai-setup";
      if (!AI_SETUP_SIDE_VIEWS.has(store.sideView)) {
        store.sideView = "ai-setup";
      }
    } else if (navView === "solver") {
      store.configSource = "manual";
      if (!SOLVER_SIDE_VIEWS.has(store.sideView)) {
        store.sideView = "oop-range";
      }
    }

    // Vue 메모리 라우터도 동기화하여 Vue 컴포넌트가 올바른 라우트를 인식하도록 함
    const router = bridgeRouterRef.current;
    if (router) {
      const targetPath = resolveVueRouterPath(navView, routePath);
      const currentPath = router.currentRoute.value.path;
      if (currentPath !== targetPath) {
        router.replace(targetPath);
      }
    }
  }, [navView, routePath]);

  return (
    <div
      ref={containerRef}
      className="vue-app-bridge"
      style={vueBridgeContainerStyle(hidden)}
    />
  );
}
